import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Box,
  Progress,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import YouTube from 'react-youtube';
import { TextQuestion, MultipleChoiceQuestion } from '../../../components/activities';
import { useCourse } from '../../../contexts/CourseContext';
import { useCourseConfig } from '../../../hooks/useCourseConfig';
import { supabase } from '../../../lib/supabase';

/**
 * Landing page with embedded questionnaire for ADHDWRITE1
 * Shows intro content, then the questionnaire, then submit button
 */
export default function LandingWithQuestionnaire() {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentCourse } = useCourse();
  const config = useCourseConfig();
  
  const [completedActivities, setCompletedActivities] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get required activities from config
  const requiredActivityKeys = config.activities?.requirements
    ?.filter(req => req.required)
    .map(req => req.activity_key) || [];

  const totalRequired = requiredActivityKeys.length;
  const completedCount = Array.from(completedActivities).filter(key => 
    requiredActivityKeys.includes(key)
  ).length;
  const progressPercent = totalRequired > 0 ? (completedCount / totalRequired) * 100 : 0;
  const allRequiredComplete = completedCount === totalRequired && totalRequired > 0;

  console.log('[LandingWithQuestionnaire] Progress:', {
    totalRequired,
    completedCount,
    completedActivities: Array.from(completedActivities),
    requiredActivityKeys,
    allRequiredComplete
  });

  // Check which activities are already completed
  useEffect(() => {
    async function checkCompletion() {
      if (!currentCourse || requiredActivityKeys.length === 0) return;
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get all activity IDs for required activities
        const { data: activities } = await supabase
          .from('learning_activities')
          .select('id, activity_key')
          .in('activity_key', requiredActivityKeys);

        if (!activities) return;

        // Check which ones have responses
        const activityIds = activities.map(a => a.id);
        const { data: responses } = await supabase
          .from('learner_responses')
          .select('activity_id, response_value')
          .eq('user_id', user.id)
          .eq('course_id', currentCourse.id)
          .in('activity_id', activityIds);

        if (responses) {
          const completed = new Set<string>();
          responses.forEach(resp => {
            // Find the activity key for this response
            const activity = activities.find(a => a.id === resp.activity_id);
            if (activity && resp.response_value && resp.response_value.trim()) {
              completed.add(activity.activity_key);
            }
          });
          setCompletedActivities(completed);
        }
      } catch (error) {
        console.error('Error checking completion:', error);
      }
    }

    checkCompletion();
  }, [currentCourse, requiredActivityKeys.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Mark activity as completed when response is saved (only if it has content)
  const handleActivityComplete = (activityKey: string, value: string | null) => {
    console.log('[handleActivityComplete]', { activityKey, value, hasContent: !!(value && value.trim()) });
    setCompletedActivities(prev => {
      const newSet = new Set(prev);
      // Only mark as complete if there's actual content
      if (value && value.trim()) {
        newSet.add(activityKey);
        console.log('[handleActivityComplete] Added to completed:', activityKey);
      } else {
        // Remove from completed if value is empty
        newSet.delete(activityKey);
        console.log('[handleActivityComplete] Removed from completed:', activityKey);
      }
      return newSet;
    });
  };

  // Get list of incomplete required activities
  const getIncompleteActivities = () => {
    const incomplete: string[] = [];
    const activityLabels: Record<string, string> = {
      'accessibility_pen_paper': 'Pen and paper writing preference',
      'accessibility_other_needs': 'Other accessibility needs',
      'writing_goals': 'What do you want to write?',
      'strategies_getting_started': 'Strategies that get you started',
      'barriers_getting_started': 'What stops you from getting started',
      'barriers_staying_engaged': 'What puts you off easily',
    };
    
    requiredActivityKeys.forEach(key => {
      if (!completedActivities.has(key)) {
        incomplete.push(activityLabels[key] || key);
      }
    });
    
    return incomplete;
  };

  // Handle submission and navigation
  const handleSubmit = async () => {
    console.log('[handleSubmit] Attempting submit...', { allRequiredComplete, completedCount, totalRequired });
    
    if (!allRequiredComplete) {
      console.log('[handleSubmit] Blocked - not all required complete');
      toast({
        title: 'Cannot submit',
        description: 'Please complete all required questions',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !currentCourse) {
        console.log('[handleSubmit] No user or course');
        return;
      }

      // Double-check by validating responses in database
      const { data: activities } = await supabase
        .from('learning_activities')
        .select('id, activity_key')
        .in('activity_key', requiredActivityKeys);

      if (!activities) {
        throw new Error('Failed to fetch activities');
      }

      const activityIds = activities.map(a => a.id);
      const { data: responses } = await supabase
        .from('learner_responses')
        .select('activity_id, response_value')
        .eq('user_id', user.id)
        .eq('course_id', currentCourse.id)
        .in('activity_id', activityIds);

      // Validate all required activities have non-empty responses
      const responseMap = new Map(responses?.map(r => [r.activity_id, r.response_value]) || []);
      const missingResponses = activities.filter(a => {
        const value = responseMap.get(a.id);
        return !value || !value.trim();
      });

      if (missingResponses.length > 0) {
        console.log('[handleSubmit] Missing responses:', missingResponses);
        toast({
          title: 'Cannot submit',
          description: `Please complete: ${missingResponses.map(a => a.activity_key).join(', ')}`,
          status: 'warning',
          duration: 4000,
        });
        setIsSubmitting(false);
        return;
      }

      // Mark all responses as submitted
      await supabase
        .from('learner_responses')
        .update({ submitted_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('course_id', currentCourse.id)
        .in('activity_id', activityIds)
        .is('submitted_at', null);

      console.log('[handleSubmit] Success! Navigating to thank you page');
      toast({
        title: 'Responses submitted!',
        status: 'success',
        duration: 2000,
      });

      // Navigate to thank you page using the step route format
      navigate('/s/thank-you');
    } catch (error) {
      console.error('[handleSubmit] Error:', error);
      toast({
        title: 'Error submitting responses',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="xl" mb={4}>
            ADHD Writing Workshop
          </Heading>
          <Text fontSize="lg" mb={4}>
            Welcome to the ADHD Writing workshop
          </Text>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            We're so glad you're here!
          </Text>
          
          <Text mb={4}>
            We want to make sure that everyone spends lots of time in the workshop on the activities that connect us and help us to be better writers.
          </Text>
          
          <Text mb={4}>
            We also know that people with ADHD often learn best at the time that works for them.
          </Text>
          
          <Text mb={4}>
            So, we want you to complete these activities online at the time that works for you <Text as="strong">at least a week before the workshop</Text>.
          </Text>
          
          <Text mb={4}>
            This means you get a chance to learn some new stuff that will help you with the workshop <Text as="strong">AND</Text> we have a chance to look at everyone's responses and create a workshop that will work for the people who are coming.
          </Text>
          
          <Text mb={4}>
            What if we find out that you all love stories about aliens from outer space?
          </Text>
          
          <Text mb={4}>
            Imagine how cool the workshop will be when we know more about who is coming?
          </Text>
          
          <Text mb={6} fontWeight="semibold">
            So, let's start with a few questions about you.
          </Text>
        </Box>

        <Divider />

        {/* Progress Bar */}
        <Box>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Progress: {completedCount} of {totalRequired} required questions completed
          </Text>
          <Progress 
            value={progressPercent} 
            colorScheme={allRequiredComplete ? 'green' : 'blue'}
            size="sm"
            borderRadius="md"
          />
        </Box>

        {/* Accessibility Section */}
        <Box>
          <Heading size="lg" mb={4}>
            Accessibility needs
          </Heading>
          
          <Box mb={6}>
            <MultipleChoiceQuestion
              activityKey="accessibility_pen_paper"
              required
              onResponseChange={(value) => handleActivityComplete('accessibility_pen_paper', value)}
            />
          </Box>
          
          <Box>
            <TextQuestion
              activityKey="accessibility_other_needs"
              required={false}
              onResponseChange={(value) => handleActivityComplete('accessibility_other_needs', value)}
            />
          </Box>
        </Box>

        <Divider />

        {/* First Video Section */}
        <Box>
          <Heading size="lg" mb={4}>
            Let's hear from Ren and Mel about writing and ADHD
          </Heading>
          <Text mb={4}>
            This is important content that we want you to know before you come along to the workshop.
          </Text>
          <Text mb={4} fontWeight="semibold">
            So, it's important that you watch and understand this before the session.
          </Text>
          
          <Box
            position="relative"
            paddingBottom="56.25%" // 16:9 aspect ratio
            height={0}
            overflow="hidden"
            borderRadius="md"
            boxShadow="md"
          >
            <YouTube
              videoId="dQw4w9WgXcQ"
              opts={{
                width: '100%',
                height: '100%',
                playerVars: {
                  autoplay: 0,
                },
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        </Box>

        <Divider />

        {/* Before Second Video Question */}
        <Box>
          <Heading size="lg" mb={4}>
            Before you go to the next video, let's learn a bit more about you
          </Heading>
          
          <TextQuestion
            activityKey="writing_goals"
            required
            onResponseChange={(value) => handleActivityComplete('writing_goals', value)}
          />
        </Box>

        <Divider />

        {/* Second Video Section */}
        <Box>
          <Heading size="lg" mb={4}>
            Motivation Bridge
          </Heading>
          
          <Box
            position="relative"
            paddingBottom="56.25%" // 16:9 aspect ratio
            height={0}
            overflow="hidden"
            borderRadius="md"
            boxShadow="md"
            mb={6}
          >
            <YouTube
              videoId="OM0Xv0eVGtY"
              opts={{
                width: '100%',
                height: '100%',
                playerVars: {
                  autoplay: 0,
                },
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
          
          <Text mb={4} fontWeight="semibold">
            Wow! Now that you've learned a bit more about motivation and ADHD, let's learn a bit more about you.
          </Text>
          
          <Text mb={6}>
            We'll use this information to help plan the activities for the workshop. But, we won't be identifying anyone in the session based on this information.
          </Text>
        </Box>

        {/* Post-Video Questions */}
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading size="md" mb={4}>
              What strategies get you started?
            </Heading>
            <TextQuestion
              activityKey="strategies_getting_started"
              required
              onResponseChange={(value) => handleActivityComplete('strategies_getting_started', value)}
            />
          </Box>

          <Divider />

          <Box>
            <Heading size="md" mb={4}>
              What stops you from even getting started?
            </Heading>
            <TextQuestion
              activityKey="barriers_getting_started"
              required
              onResponseChange={(value) => handleActivityComplete('barriers_getting_started', value)}
            />
          </Box>

          <Divider />

          <Box>
            <Heading size="md" mb={4}>
              What puts you off easily?
            </Heading>
            <TextQuestion
              activityKey="barriers_staying_engaged"
              required
              onResponseChange={(value) => handleActivityComplete('barriers_staying_engaged', value)}
            />
          </Box>
        </VStack>

        <Divider />

        {/* Submit Button */}
        <Box>
          <Button
            colorScheme="blue"
            size="lg"
            width="full"
            onClick={handleSubmit}
            isDisabled={!allRequiredComplete || isSubmitting}
            isLoading={isSubmitting}
          >
            {allRequiredComplete ? 'Submit Responses' : `Complete ${totalRequired - completedCount} more question${totalRequired - completedCount === 1 ? '' : 's'}`}
          </Button>
          
          {/* Show detailed missing questions alert */}
          {!allRequiredComplete && (
            <Alert status="warning" mt={4} borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>Required Questions Not Completed</AlertTitle>
                <AlertDescription>
                  <Text mb={2}>Please answer the following required questions:</Text>
                  <UnorderedList spacing={1}>
                    {getIncompleteActivities().map((label, idx) => (
                      <ListItem key={idx}>{label}</ListItem>
                    ))}
                  </UnorderedList>
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </Box>
      </VStack>
    </Container>
  );
}
